import { observer } from "mobx-react-lite";
import * as React from "react";
import { ContextMenuItem, Item, TreeItemIconType } from "./types";
import {
  BsChevronDown,
  BsChevronRight,
  BsDatabase,
  BsFileEarmarkCode,
  BsFileText,
  BsFiletypeSql,
  BsFolder,
  BsTable,
  BsThreeDotsVertical,
} from "react-icons/bs";
import * as styles from "./style.module.css";
import { forwardRef, useState } from "react";
import { Dropdown } from "react-bootstrap";

const iconMap = {
  [TreeItemIconType.Database]: <BsDatabase />,
  [TreeItemIconType.Table]: <BsTable />,
  [TreeItemIconType.FileEarmarkCode]: <BsFileEarmarkCode />,
  [TreeItemIconType.Folder]: <BsFolder />,
  [TreeItemIconType.File]: <BsFileText />,
  [TreeItemIconType.FileSql]: <BsFiletypeSql />,
};

const CustomToggle = forwardRef<
  HTMLSpanElement,
  { children: React.ReactNode; onClick: (e: React.MouseEvent) => void }
>(({ children, onClick }, ref) => (
  <span
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }}
  >
    {children}
  </span>
));

export interface TreeItemProps {
  item: Item;
  expandedIds: string[];
  contextMenuItems: (item: Item) => ContextMenuItem[];
  onItemClick: (item: Item) => void;
  onToggleExpanstion: (item: Item) => void;
  onContextMenu: (item: Item, contextMenuItem: ContextMenuItem) => void;
  onRenameItem: (item: Item, newName: string) => void;
}

export const TreeItem = observer((props: TreeItemProps) => {
  const {
    item,
    expandedIds,
    contextMenuItems,
    onItemClick,
    onToggleExpanstion,
    onContextMenu,
    onRenameItem,
  } = props;

  const isExpanded = expandedIds.some((_) => _ == item.id);
  const menus = contextMenuItems(item);
  const [name, setName] = useState(item.label);

  const onClick = (e: React.MouseEvent) => {
    if (
      e.target instanceof Element &&
      (e.target.classList.contains("dropdown-item") ||
        e.target.tagName == "INPUT")
    ) {
      return;
    }
    onItemClick(item);
  };

  const onToggle = (e: React.MouseEvent) => {
    onToggleExpanstion(item);
    e.preventDefault();
    e.stopPropagation();
  };

  const onSelectContextMenu = (eventKey: string | null) => {
    const menu = menus.find((_) => _.id == eventKey);
    if (menu) {
      onContextMenu(item, menu);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
        onRenameItem(item, name);
        break;
      case "Escape":
        item.isRenaming = false;
        setName(item.label);
        break;
    }
  };

  const onBlur = () => {
    onRenameItem(item, name);
  };

  return (
    <div>
      <div className={styles.treeItem} onClick={onClick}>
        <span className={styles.treeItemToggle} onClick={onToggle}>
          {(item.items ?? []).length > 0 ? (
            isExpanded ? (
              <BsChevronDown />
            ) : (
              <BsChevronRight />
            )
          ) : undefined}
        </span>
        <span className={styles.treeItemIcon}>
          {item.icon && iconMap[item.icon]}
        </span>
        {item.isRenaming ? (
          <input
            type="text"
            value={name}
            className={styles.treeItemLabelEdit}
            ref={(node) => node?.focus()}
            onChange={(e) => setName(e.currentTarget.value)}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
          />
        ) : (
          <span className={styles.treeItemLabel}>
            <span
              className={styles.treeItemLabelInner}
              style={{ flexShrink: 0 }}
            >
              {item.label}
            </span>
            {item.labelHelper && (
              <span
                className={`text-muted ${styles.treeItemLabelInner}`}
                style={{ fontSize: "0.8em", flexShrink: 1, overflow: "hidden" }}
              >
                {item.labelHelper}
              </span>
            )}
          </span>
        )}
        {menus.length > 0 && (
          <span className={styles.treeItemMenu}>
            <Dropdown onSelect={onSelectContextMenu}>
              <Dropdown.Toggle as={CustomToggle}>
                <BsThreeDotsVertical />
              </Dropdown.Toggle>
              <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }}>
                {menus.map((_) => (
                  <Dropdown.Item key={_.id} eventKey={_.id}>
                    {_.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </span>
        )}
      </div>
      {isExpanded && (
        <div style={{ marginLeft: 12 }}>
          {(item.items ?? []).map((_, i) => (
            <TreeItem
              key={i}
              item={_}
              contextMenuItems={contextMenuItems}
              expandedIds={expandedIds}
              onItemClick={onItemClick}
              onToggleExpanstion={onToggleExpanstion}
              onContextMenu={onContextMenu}
              onRenameItem={onRenameItem}
            />
          ))}
        </div>
      )}
    </div>
  );
});
